import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ModalProvider } from '@/contexts/ModalContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastContainer } from '@/components/ToastContainer';
import { AppRouter } from '@/router/AppRouter';
import { api } from '@/services/api';
import { setCurrentFrontendVersion, extractVersionNumberFromBackendJs } from '@/services/version-service';

// Import CSS
import '/Content/Compiled.css';

// Declare version constants from orna-version.js / orna-backend-version.js
declare const ORNA_CLIENT_PORTAL_VERSION: number;
declare const ORNA_BACKEND_VERSION: number;

// Initialize API backend version
function initApp() {
  // Set frontend version
  try {
    setCurrentFrontendVersion(ORNA_CLIENT_PORTAL_VERSION);
  } catch {
    // Version constant may not be available in dev
  }

  // Set backend version - first try fetching latest, fallback to bundled constant
  const apiReady = api.backendVersionCheck().then(
    (result) => {
      api.setCurrentBackendVersion(extractVersionNumberFromBackendJs(result));
    },
    () => {
      try {
        api.setCurrentBackendVersion(ORNA_BACKEND_VERSION);
      } catch {
        api.setCurrentBackendVersion(199); // Fallback
      }
    }
  );
  api.setApiReadyPromise(apiReady);

  // Global error handler
  window.onerror = (msg, url, lineNo, columnNo, error) => {
    let stack = '';
    if (error?.stack) stack = error.stack;
    const message = `Message: ${msg}\nURL: ${url}\nLine: ${lineNo}\nColumn: ${columnNo}\nStack: ${stack}`;
    api.insertNewJSError(message, url as string, msg as string, 'UNCAUGHT');
  };
}

initApp();

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <ModalProvider>
          <AuthProvider>
            <AppRouter />
            <ToastContainer />
          </AuthProvider>
        </ModalProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('app')!);
root.render(<App />);
